import React from 'react';
import { Menu, MenuItem, ListItemIcon, ListItemText, MenuProps } from '@mui/material';

export interface ActionMenuItem {
  /** Unique key tracking the item row */
  id: string;
  /** Primary visible string label text */
  label: string;
  /** Svg Icon component to slot on the left layout axis (e.g. <EditIcon fontSize="small" />) */
  icon?: React.ReactNode;
  /** Individual click handler callback executed when this item row gets picked */
  onClick: () => void;
  /** Optional flag to visually disable a single action row */
  disabled?: boolean;
}

export interface ActionMenuProps extends Omit<MenuProps, 'open'> {
  /** The source DOM element anchoring the popover overlay placement layout */
  anchorEl: HTMLElement | null;
  /** Handler fired to clear the current active anchor anchorEl state context */
  onClose: () => void;
  /** Custom configurations array plotting out individual dynamic rows to display */
  items: ActionMenuItem[];
}

/**
 * A configurable floating contextual dropdown menu built with standardized corporate layout styles.
 * Accepts polymorphic configuration arrays to execute distinct localized page state actions cleanly.
 *
 * @component
 * @example
 * ```tsx
 * <ActionMenu anchorEl={anchor} onClose={handleClose} items={menuConfig} />
 * ```
 */
export const ActionMenu = ({ anchorEl, onClose, items, sx, ...rest }: ActionMenuProps) => {
  const isOpen = Boolean(anchorEl);

  return (
    <Menu
      {...rest}
      anchorEl={anchorEl}
      open={isOpen}
      onClose={onClose}
      elevation={1}
      sx={{
        borderRadius: 2,
        boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.08)',
        minWidth: 180,
        mt: 0.5,
        ...sx
      }}>
      {items.map((item) => (
        <MenuItem
          key={item.id}
          disabled={item.disabled}
          onClick={() => {
            onClose();
            item.onClick();
          }}
          sx={{ typography: 'body2', py: 1 }}>
          {item.icon && (
            <ListItemIcon sx={{ minWidth: '32px !important' }}>{item.icon}</ListItemIcon>
          )}
          <ListItemText primary={item.label} primaryTypographyProps={{ variant: 'body2' }} />
        </MenuItem>
      ))}
    </Menu>
  );
};

export default ActionMenu;
